import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { NGXLogger } from "ngx-logger";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { ActionState } from "src/app/enums/action-state.enum";
import { CoreEnum } from "src/app/enums/core.enum";
import { ModalDeleteConfigData } from "src/app/models/modalDeleteConfigData";
import { StationsRepo } from "src/app/repositories/stations/repo/station-repo";
import { AlertService } from "src/app/services";
import { ExendedOpcuaConfig } from "src/app/shared/models/station/extended-opcua-config";
import {
  ApplicationFeature, Energy, EnergyType, OpcuaConfig,
  OpcuaConfigUpdate,
  SecurityMode,
  SecurityPolicy, Station, StationHealth, StationState
} from "src/swagger-gen/web-secured/models";
import { AlertParameters, AlertType } from "../../../models/models-index";
import { ModalDeleteStationConfigComponent } from "../../modal-delete-station-config/modal-delete-station-config.component";
import { DataAfterCloseDeleteStationConfigModal } from "../../modal-delete-station-config/models/dataAfterCloseDeleteStationConfigModal";

@Component({
    selector: "app-configuration-station",
    templateUrl: "./configuration-station.component.html",
    styleUrls: ["./configuration-station.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ConfigurationStationComponent implements OnInit, OnDestroy {
  @Input() index: number;
  @Input() config: ExendedOpcuaConfig;
  @Input() creationView: boolean;
  @Input() station: Station;
  @Output() reloadConfigOpcuaEvent = new EventEmitter<any>();

  FEATURE = ApplicationFeature;
  destroyed$ = new Subject<void>();

  stationState = StationState;
  prefixUrl = CoreEnum.OPCUA_URL_PREFIX;

  backendResponseDownloadedFile: any = null;
  backendResponseUploadedFile: any = null;

  // nodeRegExp = new RegExp(/(ns=.+);((i|s|g|b)=.+)/);

  formStatus = new BehaviorSubject(ActionState.READY);
  actionState = ActionState;
  subscriptions = new Subscription();
  
  isEditting = true;

  securityModeList = Object.values(SecurityMode);
  securityPolicyList = Object.values(SecurityPolicy);
  activateActionList = ["ACTIVATE", "DEACTIVATE"];

  configForm = this.fb.group({
    name: ["", Validators.required],
    active: [""],
    uri: ["", Validators.required],
    node: [""],
    security: this.fb.group({
      mode: [""],
      policy: [""],
    }),
    credentials: this.fb.group({
      login: [""],
      password: [""],
    }),
    acquisitionLostDelay: [""],
  });
  opcuaUid: string;
  eUid: string;
  isCollapsed = true;
  connectionState: String;

  get f() {
    return this.configForm.controls;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private stationsRepo: StationsRepo,
    private translate: TranslateService,
    private alertService: AlertService,
    private logger: NGXLogger,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.getStationHealth();
    this.patchStationValue(this.config);
    this.eUid = this.station?.uid;
    if (!this.creationView && !this.config.newConnection) {
      this.configForm.disable();
      this.isEditting = false;
    }
  }

  getStationHealth() {
    this.stationsRepo
      .getStationHealth(this.station?.uid)
      .toPromise()
      .then((stationHealth: StationHealth) => {
        if (this.config.node?.parentNodeId?.includes('DerivedTags')) {
          this.connectionState = stationHealth.dtComState ?? 'UNKNOWN';
        } else {
          this.connectionState = stationHealth.stationComState ?? 'UNKNOWN';
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }

  // Patch value from H2 station
  patchStationValue(config: OpcuaConfig) {
    if (config) {
      this.configForm.patchValue({
        uid: config.uid,
        name: config.name || "",
        state: config.state,
        active:
          config.active === true
            ? this.activateActionList[0]
            : this.activateActionList[1],
        uri: config.uri ? this.convertUrlValue(config.uri) : "",
        node: config.node?.parentNodeId || "",
        security: {
          mode: config.security?.mode || this.securityModeList[0],
          policy: config.security?.policy || this.securityPolicyList[0],
        },
        credentials: {
          login: config.credentials?.login || "",
          password: config.credentials?.password || "",
        },
        acquisitionLostDelay:
          config.aquisitionLostDelay ||
          CoreEnum.OPCUA_ACQUISITION_LOST_DELAY_DEFAULT,
      });
      this.opcuaUid = config.uid;
    }
  }

  convertUrlValue(uriValue: string) {
    return uriValue.replace(CoreEnum.OPCUA_URL_PREFIX, "");
  }

  toggleEdition(isCancel?: boolean) {
    this.isEditting = !this.isEditting;
    this.configForm.disabled
      ? this.configForm.enable()
      : this.configForm.disable();

    if (isCancel) {
      this.patchStationValue(this.config);
    }
  }

  onUserWantsDownloadEvent(event: boolean): void {
    if (event) {
      this.downloadFile();
    }
  }

  onUserWantsUploadEvent(file: any): void {
    if (file) {
      this.uploadFile(file);
    }
  }

  downloadFile() {
    let isGNL: boolean;
    this.station.energies.forEach((energie: Energy) => {
      if (energie.type === EnergyType.GNL || energie.type === EnergyType.GNCL) {
        isGNL = true;
        return;
      }
    });
    this.stationsRepo
      .exportConfigTableExport(this.eUid, this.opcuaUid, isGNL)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.backendResponseDownloadedFile = response;
        },
        error: (error) => {
          this.backendResponseDownloadedFile = error;
        },
      });
  }

  uploadFile(file: any): void {
    let isGNL: boolean;
    this.station.energies.forEach((energie: Energy) => {
      if (energie.type === EnergyType.GNL || energie.type === EnergyType.GNCL) {
        isGNL = true;
        return;
      }
    });
    this.stationsRepo
      .importConfigTableExport(this.eUid, this.opcuaUid, file, isGNL)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (response) => {
          this.backendResponseUploadedFile = response;
        },
        (error) => {
          this.backendResponseUploadedFile = error;
        }
      );
  }

  onSubmit() {
    this.configForm.markAllAsTouched();
    if (this.configForm.valid) {
      this.formStatus.next(ActionState.LOADING);
      const formValue = this.configForm.value;

      const body = this.preparingBodyToRequest(formValue);

      // Update existing OPCUA connection
      if (this.opcuaUid) {
        this.stationsRepo
          .updateConfigurationOpcua(this.eUid, this.opcuaUid, body)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: (res) => {
              this.formStatus.next(ActionState.READY);
              this.reloadConfigOpcuaEvent.next(
                this.translate.instant(
                  "stations.form.message.success.config_updated"
                )
              );
            },
            error: (error) => {
              this.formStatus.next(ActionState.READY);
              this.showAlert(error);
            },
          });
      } else {
        // Create new OPCUA connection
        this.stationsRepo
          .createConfigurationOpcua(this.eUid, body)
          .pipe(takeUntil(this.destroyed$))
          .subscribe({
            next: (res) => {
              this.formStatus.next(ActionState.READY);
              this.reloadConfigOpcuaEvent.next(
                this.translate.instant(
                  "stations.form.message.success.config_created"
                )
              );
            },
            error: (error) => {
              this.formStatus.next(ActionState.READY);
              this.showAlert(error);
            },
          });
      }
    }
  }

  preparingBodyToRequest(formValue: any): OpcuaConfigUpdate {
    let body: OpcuaConfigUpdate;
    body = {
      name: formValue.name,
      active: formValue.active === "ACTIVATE" ? true : false,
      security: {
        mode: formValue.security.mode,
        policy: formValue.security.policy,
      },
      credentials: {
        login: formValue.security.login,
        password: formValue.security.password,
      },
      aquisitionLostDelay: formValue.acquisitionLostDelay,
      uri: CoreEnum.OPCUA_URL_PREFIX + formValue.uri,
      node: { parentNodeId: formValue.node },
    };
    return body;
  }

  showAlert(error: any) {
    const title = this.translate.instant("error.title");
    const alertParams = new AlertParameters(
      false,
      false,
      true,
      false,
      false,
      false,
      true,
      title
    );
    this.alertService.showAlert(error, AlertType.ERROR, alertParams);
  }

  onDeleteConfig(): void {
    this.openDeleteDialog();
  }

  openDeleteDialog() {
    let dialogRef: any;
    const data = new ModalDeleteConfigData();
    data.type = "opcua";
    data.config = this.config;
    data.stationUid = this.station.uid;
    const configDialog = {
      data: data,
    };
    dialogRef = this.dialog.open(
      ModalDeleteStationConfigComponent,
      configDialog
    );

    if (dialogRef) {
      dialogRef
        .afterClosed()
        .subscribe((result: DataAfterCloseDeleteStationConfigModal) => {
          if (result?.successDelete) {
            this.formStatus.next(ActionState.READY);
            this.reloadConfigOpcuaEvent.next(
              this.translate.instant("stationConfig.delete.success")
            );
          }
        });
    }
    return;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
